/**
 * Single capture anchor pin + DOM label (GIS alignment origin on ground plane).
 * World-layer geometry with depth write so it occludes/blocks like other world meshes.
 */
import {
    BLEND_NORMAL,
    CULLFACE_NONE,
    Entity,
    Mesh,
    MeshInstance,
    PRIMITIVE_LINES,
    StandardMaterial,
    Vec3,
    type GraphicsDevice
} from 'playcanvas';

import type { Events } from './events';
import type { Scene } from './scene';

const _vcScreen = new Vec3();

function getVolugraphScene(): Scene | null {
    const w = window as unknown as { scene?: Scene };
    return w.scene ?? null;
}

function createPinStemMesh(device: GraphicsDevice, stemWorld: number): Mesh {
    const mesh = new Mesh(device);
    const h = Math.max(0.02, stemWorld);
    const positions = new Float32Array([
        0, 0, 0,
        0, h, 0
    ]);
    mesh.setPositions(positions);
    mesh.update(PRIMITIVE_LINES);
    return mesh;
}

type CapturePinState = {
    entity: Entity;
    mesh: Mesh;
    material: StandardMaterial;
    hitEl: HTMLButtonElement;
    labelEl: HTMLDivElement;
    world: Vec3;
    stemH: number;
    lat: number;
    lng: number;
};

let pinState: CapturePinState | null = null;
let markersHost: HTMLDivElement | null = null;
let postrenderOff: (() => void) | null = null;

function ensureMarkersHost(): HTMLDivElement | null {
    const canvasContainer = document.getElementById('canvas-container');
    if (!canvasContainer) return null;
    if (!markersHost) {
        const el = document.createElement('div');
        el.className = 'volugraph-capture-pin-host';
        el.style.cssText =
            'position:absolute;left:0;top:0;width:100%;height:100%;pointer-events:none;z-index:37;';
        canvasContainer.appendChild(el);
        markersHost = el;
    }
    return markersHost;
}

function destroyCapturePinInternal(): void {
    if (postrenderOff) {
        try {
            postrenderOff();
        } catch {
            /* ignore */
        }
        postrenderOff = null;
    }
    const s = pinState;
    pinState = null;
    if (!s) {
        if (markersHost && markersHost.childNodes.length === 0) {
            try {
                markersHost.remove();
            } catch {
                /* ignore */
            }
            markersHost = null;
        }
        return;
    }
    try {
        s.entity.destroy();
    } catch {
        /* ignore */
    }
    try {
        s.mesh.destroy();
    } catch {
        /* ignore */
    }
    try {
        s.material.destroy();
    } catch {
        /* ignore */
    }
    try {
        s.hitEl.remove();
    } catch {
        /* ignore */
    }
    try {
        s.labelEl.remove();
    } catch {
        /* ignore */
    }
    if (markersHost && markersHost.childNodes.length === 0) {
        try {
            markersHost.remove();
        } catch {
            /* ignore */
        }
        markersHost = null;
    }
}

function updatePinLayout(scene: Scene): void {
    const s = pinState;
    if (!s || !markersHost) return;
    const w = scene.canvas.clientWidth;
    const h = scene.canvas.clientHeight;
    scene.camera.worldToScreen(s.world, _vcScreen);
    if (
        _vcScreen.z <= 0 ||
        !Number.isFinite(_vcScreen.x) ||
        !Number.isFinite(_vcScreen.y)
    ) {
        s.hitEl.classList.add('volugraph-capture-pin-hidden');
        s.labelEl.classList.add('volugraph-capture-pin-hidden');
        return;
    }
    const sx = _vcScreen.x;
    const sy = _vcScreen.y;
    if (sx < -0.05 || sx > 1.05 || sy < -0.05 || sy > 1.05) {
        s.hitEl.classList.add('volugraph-capture-pin-hidden');
        s.labelEl.classList.add('volugraph-capture-pin-hidden');
        return;
    }
    s.hitEl.classList.remove('volugraph-capture-pin-hidden');
    s.labelEl.classList.remove('volugraph-capture-pin-hidden');
    const px = sx * w;
    const py = sy * h;
    s.hitEl.style.left = `${px}px`;
    s.hitEl.style.top = `${py}px`;
    s.labelEl.style.left = `${px + 16}px`;
    s.labelEl.style.top = `${py - 10}px`;
}

function capturePinUpsertImpl(opts: {
    position: [number, number, number];
    lat?: number;
    lng?: number;
    label?: string;
}): boolean {
    const scene = getVolugraphScene();
    if (!scene?.app?.graphicsDevice || !scene.contentRoot || !scene.worldLayer) return false;

    const pos = opts?.position;
    if (!Array.isArray(pos) || pos.length !== 3 || !pos.every((n) => typeof n === 'number' && Number.isFinite(n))) {
        return false;
    }

    destroyCapturePinInternal();

    const host = ensureMarkersHost();
    if (!host) return false;

    const sr = Math.max(scene.camera.sceneRadius, 1e-4);
    const stemH = sr * 0.035;

    const device = scene.app.graphicsDevice;
    const mesh = createPinStemMesh(device, stemH);
    const material = new StandardMaterial();
    material.cull = CULLFACE_NONE;
    material.diffuse.set(0, 0, 0);
    material.emissive.set(0.35, 0.75, 1);
    material.emissiveIntensity = 1.1;
    material.useLighting = false;
    material.depthTest = true;
    material.depthWrite = true;
    material.blendType = BLEND_NORMAL;
    material.opacity = 1;
    material.update();

    const meshInstance = new MeshInstance(mesh, material);
    const entity = new Entity('volugraphCapturePin', scene.app);
    entity.addComponent('render', {
        meshInstances: [meshInstance],
        layers: [scene.worldLayer.id]
    });
    scene.contentRoot.addChild(entity);

    const world = new Vec3(pos[0], pos[1], pos[2]);
    entity.setPosition(world.x, world.y, world.z);
    const scale = sr * 0.012;
    entity.setLocalScale(scale, scale, scale);

    const hitEl = document.createElement('button');
    hitEl.type = 'button';
    hitEl.className = 'volugraph-capture-pin-hit';
    hitEl.setAttribute('aria-label', 'Map plot anchor — click to copy coordinates');
    hitEl.style.cssText =
        'position:absolute;width:22px;height:22px;margin:-11px 0 0 -11px;border-radius:50%;' +
        'border:2px solid rgba(120,200,255,0.95);background:rgba(40,90,140,0.35);cursor:pointer;padding:0;pointer-events:auto;';
    host.appendChild(hitEl);

    const lat = typeof opts.lat === 'number' && Number.isFinite(opts.lat) ? opts.lat : NaN;
    const lng = typeof opts.lng === 'number' && Number.isFinite(opts.lng) ? opts.lng : NaN;
    const title = typeof opts.label === 'string' && opts.label.trim() ? opts.label.trim() : 'Capture';

    const labelEl = document.createElement('div');
    labelEl.className = 'volugraph-capture-pin-label';
    labelEl.style.cssText =
        'position:absolute;pointer-events:none;max-width:220px;line-height:1.25;' +
        'font-family:Inter,system-ui,sans-serif;font-size:11px;color:#fff;' +
        'text-shadow:0 0 4px #000,0 1px 2px #000;padding:6px 8px;' +
        'background:rgba(12,20,32,0.85);border:1px solid rgba(120,180,255,0.45);border-radius:8px;';
    labelEl.textContent = `${title}\n${Number.isFinite(lat) && Number.isFinite(lng) ? `${lat.toFixed(5)}, ${lng.toFixed(5)}` : '—'}`;
    host.appendChild(labelEl);

    hitEl.addEventListener('click', () => {
        if (Number.isFinite(lat) && Number.isFinite(lng)) {
            const t = `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
            void navigator.clipboard?.writeText?.(t);
        }
    });

    pinState = {
        entity,
        mesh,
        material,
        hitEl,
        labelEl,
        world,
        stemH,
        lat,
        lng
    };

    const handle = () => updatePinLayout(scene);
    const sub = scene.events.on('postrender', handle);
    postrenderOff = () => {
        try {
            sub?.off?.();
        } catch {
            /* ignore */
        }
    };

    scene.forceRender = true;
    return true;
}

export function registerVolugraphCapturePin(events: Events): void {
    events.function(
        'volugraph.capturePinUpsert',
        (opts: { position: [number, number, number]; lat?: number; lng?: number; label?: string }) =>
            capturePinUpsertImpl(opts)
    );
    events.function('volugraph.capturePinRemove', () => {
        destroyCapturePinInternal();
        const sc = getVolugraphScene();
        if (sc) sc.forceRender = true;
    });
}
